import { LencoTypography } from '@/atomic-components/LencoTypography';
import { ICartItem } from '@/services/cart/models/cart';
import { getColor } from '@/utils/theme/colors';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Divider,
	IconButton,
} from '@mui/material';
import CartItem from './OOSCartItem';
import CartItemListHeader from '../VerticalCart/CartItemsListHeader';
import LencoButton from '@/atomic-components/LencoButton';
import { getStyle } from '@/utils/theme/fonts';
import ArrowWithoutTail from '@/assets/ArrowWithoutTail';
import { IProductData } from '@/components/search-results/model';
import AddonProductTile from '@/components/AddonProductTile';
import FlexBox from '@/atomic-components/FlexBox';
import { useState } from 'react';
import { PdpUrlHelper } from '@/utils/seo-url-helper';
import ReplaceIcon from '@/assets/actions/ReplaceIcon32x32.svg';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';
import { ApiState, BaseAxiosApiError } from '@/models/generics';
import { cartService } from '@/services/cart';
import { getCartContext, getCartTypeFromLop } from '@/utils/cart';
import { useLencoDispatch } from '@/hooks/useLencoDispatch';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useCart } from '@/hooks/useCart';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';

type ReplaceWithSimilarButtonProps = {
	onClick: () => void;
	isSelected?: boolean;
};

const ReplaceWithSimilarButton = (props: ReplaceWithSimilarButtonProps) => (
	<LencoButton
		variant='contained'
		color='secondary'
		disableElevation
		sx={{
			...getStyle('Tiny_Medium'),
			p: '8px 8px',

			':hover': {
				background: 'white',
				color: getColor('Minty 0'),
			},
		}}
		onClick={(e) => {
			e.stopPropagation();
			e.preventDefault();
			props.onClick();
		}}
	>
		Replace with similar item
		<span
			style={{
				marginLeft: '4px',
				transform: props.isSelected ? 'rotate(-180deg)' : 'rotate(0deg)',
			}}
		>
			<ArrowWithoutTail color={getColor('Minty 0')} height={6} width={10} />
		</span>
	</LencoButton>
);

type RelatedItemsListProps = {
	item: ICartItem;
	relatedProducts?: Record<string, IProductData[]>;
	isVisible: boolean;
	onReplace: (
		replacedItem: { id: number },
		replacingItem: { id: number; tenure?: number }
	) => void;
};

const RelatedItemsList = (props: RelatedItemsListProps) => {
	const getRelatedProductsForOosItem = (item: ICartItem): IProductData[] => {
		if (
			props.relatedProducts &&
			Array.isArray(props.relatedProducts[item.product.permalink])
		) {
			return props.relatedProducts[item.product.permalink];
		}
		return [];
	};

	return (
		<Accordion
			disableGutters
			expanded={props.isVisible}
			sx={{
				'.MuiAccordion-gutters': {
					background: 'white !important',
				},
			}}
		>
			<AccordionSummary
				sx={{
					visibility: 'hidden',
					height: '0px',
					minHeight: '0px !important',
				}}
			></AccordionSummary>
			<AccordionDetails sx={{ padding: '0px' }}>
				<FlexBox
					sx={{
						overflow: 'scroll',
						width: '100%',
						'.addon-productile-product-image': {
							width: '60px',
							margin: 'auto',
						},
						'.addon-productile-product-title': {
							WebkitLineClamp: 1,
						},
					}}
				>
					{getRelatedProductsForOosItem(props.item).map((relatedProduct) => (
						<Link
							key={relatedProduct.permalink}
							href={PdpUrlHelper({
								lop: relatedProduct.lineOfProduct,
								permalink: relatedProduct.permalink,
							})}
							target='_blank'
						>
							<Box
								key={relatedProduct.permalink}
								mr='8px'
								sx={{
									'.MuiAccordionDetails-root': {
										boxShadow: 'none',
									},
								}}
							>
								<AddonProductTile
									product={{
										...relatedProduct,
										permalink: PdpUrlHelper({
											lop: relatedProduct.lineOfProduct,
											permalink: relatedProduct.permalink,
										}),
									}}
								/>
								<Box
									sx={{
										position: 'relative',
										right: '-85%',
										bottom: '40px',
									}}
								>
									<IconButton
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
											props.onReplace(
												{ id: props.item.id },
												{
													id: relatedProduct.id,
													...(props.item.tenure && {
														tenure: props.item.tenure,
													}),
												}
											);
										}}
									>
										<ReplaceIcon />
									</IconButton>
								</Box>
							</Box>
						</Link>
					))}
				</FlexBox>
			</AccordionDetails>
		</Accordion>
	);
};

type VerticalBasedOosCartItemsListProps = {
	title: string;
	items: ICartItem[];
	relatedProducts?: Record<string, IProductData[]>;
};

export const VerticalBasedOosCartItemsList = (
	props: VerticalBasedOosCartItemsListProps
) => {
	const [
		itemSelectedToViewRelatedProducts,
		setItemSelectedToViewRelatedProducts,
	] = useState<number>();
	const [replaceCartItemApiState, setReplaceCartItemApiState] =
		useState<ApiState>(ApiState.IDLE);

	const dispatch = useLencoDispatch();
	const router = useRouter();
	const { loader } = useFullScreenLoader();
	const { cartActions } = useCart();
	const {
		actions: { notifyUser },
	} = useNotification();

	const showRelatedProductsClicked = (item: ICartItem) => {
		if (itemSelectedToViewRelatedProducts == item.id) {
			setItemSelectedToViewRelatedProducts(undefined);
		} else {
			setItemSelectedToViewRelatedProducts(item.id);
		}
	};

	const canShowRelatedItemsForThisProduct = (item: ICartItem) => {
		const isCurrentItemSelected = item.id == itemSelectedToViewRelatedProducts;

		return isCurrentItemSelected;
	};

	const replaceCartItem = async (
		replacedItem: { id: number },
		replacingItem: { id: number; tenure?: number }
	) => {
		setReplaceCartItemApiState(ApiState.LOADING);
		loader.show();

		await cartService
			.replaceCartItem(replacedItem.id, {
				context: getCartContext(router),
				id: replacingItem.id,
				...(replacingItem.tenure && { tenure: replacingItem.tenure }),
			})
			.then((res) => {
				cartActions.getCart();

				notifyUser({
					message: res.data.data?.message,
					severity: NotificationSeverity.SUCCESS,
				});
			})
			.catch((e) => {
				const error = e as BaseAxiosApiError;

				notifyUser({
					message: error.response?.data?.error?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => {
				setReplaceCartItemApiState(ApiState.IDLE);
				loader.hide();
			});
	};

	const removeCartItem = (item: ICartItem) => {
		loader.show();

		cartActions
			.removeItemFromCart(
				item?.id as number,
				getCartTypeFromLop(item.lineOfProduct)
			)
			.then((res) => {
				notifyUser({
					message: res.data.data.message,
					severity: NotificationSeverity.SUCCESS,
				});
			})
			.catch((e) => {
				const error = e as BaseAxiosApiError;
				notifyUser({
					message: error.response?.data.error?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => {
				loader.hide();
			});
	};

	const canViewRelatedProductsButton = (permalink: string) => {
		return (
			props.relatedProducts &&
			Array.isArray(props.relatedProducts[permalink]) &&
			props.relatedProducts[permalink].length > 0
		);
	};

	return (
		<Box
			mb={'24px'}
			sx={{
				borderRadius: '16px',
				border: '1px solid #E8E8E8',
				overflow: 'hidden',
			}}
		>
			<CartItemListHeader
				headerLabel={props.title}
				headerCount={props.items.length}
			/>
			{props.items.map((item) => {
				return (
					<Box
						key={item.id}
						sx={{
							'.cart-item-image': {
								width: '80px',
								minWidth: '80px',
							},
						}}
					>
						<Divider />
						<Link
							href={PdpUrlHelper({
								lop: item.lineOfProduct,
								permalink: item.product.permalink,
							})}
							target='_blank'
						>
							<CartItem
								item={item}
								onDeleteClicked={() => removeCartItem(item)}
								isDrawer={true}
								Actions={
									<FlexBox>
										<Box>
											<LencoTypography
												variant='Tiny_Medium'
												color={getColor('Danger -1')}
												m='8px 0px'
											>
												OUT OF STOCK
											</LencoTypography>
											{canViewRelatedProductsButton(item.product.permalink) && (
												<ReplaceWithSimilarButton
													onClick={() => showRelatedProductsClicked(item)}
													isSelected={
														itemSelectedToViewRelatedProducts == item.id
													}
												></ReplaceWithSimilarButton>
											)}
										</Box>
									</FlexBox>
								}
								Footer={
									<RelatedItemsList
										item={item}
										relatedProducts={props.relatedProducts}
										isVisible={canShowRelatedItemsForThisProduct(item)}
										onReplace={replaceCartItem}
									/>
								}
							/>
						</Link>
					</Box>
				);
			})}
		</Box>
	);
};
